import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import AppAutocomplete from "./AppAutocomplete";
import { api } from "../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * 공용 사원 자동완성 — 메일 받는이 자동완성 디자인을 표준으로 채택.
 *
 * <h3>왜 필요한가</h3>
 * 채널 멤버/멘션, 결재선, HR 필터, 캘린더 필터 등 사원 검색이 4가지 다른 구현으로 분산되어 있어
 * 같은 도메인 선택의 부수효과(알람·권한·검증)가 화면마다 다르게 동작할 위험이 있다. 본 컴포넌트는
 * "사원 자동완성"을 한 곳으로 통일하기 위한 표준 진입점이다.
 *
 * <h3>디자인 표준</h3>
 * 메일 ComposeModal 의 AddressAutocomplete 와 시각 토큰 1:1 일치:
 * <ul>
 *   <li><b>옵션 행</b> — 1행: 이름(13/600) + 같은 줄에 부서·직책(11/회색/400) / 2행: 이메일(11/회색)</li>
 *   <li><b>칩</b> — bgcolor #eff6ff, color #1e40af, height 22, fontSize 11, fontWeight 500</li>
 *   <li><b>TextField</b> — variant="standard" + disableUnderline (기본). 사용처에서 override 가능</li>
 * </ul>
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code multiple} — 단일/다중 선택. 기본 false.</li>
 *   <li>{@code value, onChange(newValue)}</li>
 *   <li>{@code label, placeholder, autoFocus, disabled, helperText}</li>
 *   <li>{@code excludeIds=[]} — 결과에서 제외할 사원 id (이미 선택된 사람 등)</li>
 *   <li>{@code excludeSelf=false / selfId} — 본인 제외 (selfId 가 있으면 함께 사용)</li>
 *   <li>{@code size=8} — fetch 개수 (백엔드 size 파라미터)</li>
 *   <li>{@code variant="standard"|"outlined"} — 입력 칸 외형. 기본 "standard" + disableUnderline</li>
 *   <li>{@code sx, fullWidth}</li>
 * </ul>
 *
 * <h3>확장 hooks</h3>
 * 도메인별 부수효과(알람 발송 / 권한 체크 등)는 호출 측의 {@code onChange} 안에서 처리한다.
 * 이 컴포넌트는 표준 UI · 표준 fetcher 만 책임진다.
 */
export default function EmployeeAutocomplete({
    multiple = false,
    value,
    onChange,
    label,
    placeholder = "이름·이메일 검색",
    autoFocus = false,
    disabled = false,
    helperText,

    excludeIds = [],
    excludeSelf = false,
    selfId,
    size = 8,

    variant = "standard",
    disableUnderline = true,
    fullWidth = true,
    sx,
}) {
    const excludeSet = new Set(excludeIds || []);
    if (excludeSelf && selfId != null) excludeSet.add(selfId);

    const fetcher = async (q) => {
        try {
            const res = await api.get(`${API_BASE}/employees/suggest`, { q, size });
            const data = res?.data ?? res ?? [];
            const list = Array.isArray(data) ? data : [];
            return excludeSet.size > 0 ? list.filter(e => !excludeSet.has(e.id)) : list;
        } catch {
            return [];
        }
    };

    return (
        <AppAutocomplete
            multiple={multiple}
            value={value}
            onChange={onChange}
            fetcher={fetcher}
            label={label}
            placeholder={placeholder}
            autoFocus={autoFocus}
            disabled={disabled}
            helperText={helperText}
            variant={variant}
            disableUnderline={disableUnderline}
            fullWidth={fullWidth}
            sx={sx}
            getOptionLabel={(o) => (typeof o === "string" ? o : (o?.name || ""))}
            isOptionEqualToValue={(a, b) => {
                if (a == null || b == null) return false;
                if (typeof a === "string" || typeof b === "string") return a === b;
                return a.id === b.id;
            }}
            renderOption={(props, option) => {
                const { key, ...rest } = props;
                const k = option.id ?? option.employeeCode ?? option.email;
                const meta = [option.department, option.position].filter(Boolean).join(" · ");
                return (
                    <Box component="li" key={k} {...rest}
                        sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", py: 0.5, px: 1.2 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                            {option.name}
                            {meta && (
                                <Box component="span" sx={{ ml: 1, fontSize: 11, color: "#64748b", fontWeight: 400 }}>
                                    {meta}
                                </Box>
                            )}
                        </Typography>
                        {option.email && (
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>{option.email}</Typography>
                        )}
                    </Box>
                );
            }}
            renderTag={(v, i, getTagProps) => {
                const lbl = typeof v === "string" ? v : (v?.name || v?.email || "");
                return (
                    <Chip
                        key={i}
                        label={lbl}
                        size="small"
                        {...getTagProps({ index: i })}
                        sx={{
                            fontSize: 11, height: 22, fontWeight: 500,
                            bgcolor: "#eff6ff", color: "#1e40af",
                            "& .MuiChip-deleteIcon": { color: "#1e40af" },
                        }}
                    />
                );
            }}
        />
    );
}
